import React from 'react';
import { Download, Github, Linkedin, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { getResumeDownloadUrl } from '../services/api';
import { usePortfolioData } from '../contexts/PortfolioDataContext';

const Hero = () => {
  const { profile: personalInfo, projects, experience, loading } = usePortfolioData();

  const handleDownloadResume = () => {
    const resumeUrl = getResumeDownloadUrl(personalInfo);
    if (!resumeUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = resumeUrl;
    link.download = personalInfo?.resumeFileName || 'resume';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="home" className="theme-page-gradient min-h-screen flex items-center justify-center">
        <div className="theme-accent-text text-xl">Loading...</div>
      </section>
    );
  }

  if (!personalInfo) {
    return (
      <section id="home" className="theme-page-gradient min-h-screen flex items-center justify-center">
        <div className="text-xl" style={{ color: 'var(--site-error)' }}>Failed to load profile data</div>
      </section>
    );
  }

  const hasResume = Boolean(getResumeDownloadUrl(personalInfo));
  const derivedProjectCount = Array.isArray(projects) ? projects.length : 0;
  const derivedCompanyCount = Array.isArray(experience)
    ? new Set(experience.map((item) => item?.company).filter(Boolean)).size
    : 0;
  const yearsOfExperience = Number(personalInfo.yearsOfExperience) || 0;
  const projectsCompleted = Number(personalInfo.projectsCompleted) > 0 ? Number(personalInfo.projectsCompleted) : derivedProjectCount;
  const companiesWorked = Number(personalInfo.companiesWorked) > 0 ? Number(personalInfo.companiesWorked) : derivedCompanyCount;
  const formattedYearsOfExperience = Number.isInteger(yearsOfExperience)
    ? `${yearsOfExperience}+`
    : `${yearsOfExperience.toFixed(1)}+`;

  return (
    <section id="home" className="theme-page-gradient min-h-screen flex items-center justify-center relative overflow-hidden pt-24 sm:pt-28">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full blur-3xl animate-pulse sm:h-80 sm:w-80 lg:h-96 lg:w-96" style={{ backgroundColor: 'color-mix(in srgb, var(--site-accent-primary) 18%, transparent)' }}></div>
        <div className="absolute bottom-1/4 right-1/4 h-64 w-64 rounded-full blur-3xl animate-pulse sm:h-80 sm:w-80 lg:h-96 lg:w-96" style={{ backgroundColor: 'color-mix(in srgb, var(--site-accent-secondary) 16%, transparent)', animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10 w-full">
        <div className="text-center">
          {/* Status indicator */}
          <div className="inline-flex max-w-full items-center gap-2 px-3 py-2 sm:px-4 theme-surface rounded-full mb-6 sm:mb-8 backdrop-blur-sm">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--site-success)' }}></div>
            <span className="theme-accent-text text-[11px] sm:text-sm font-semibold tracking-[0.18em] sm:tracking-[0.22em]">
              ONLINE • AVAILABLE FOR OPPORTUNITIES
            </span>
          </div>

          {/* Name and Title */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold theme-text-primary theme-heading-font mb-4 break-words">
              {personalInfo.name}
            </h1>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-2 theme-accent-text text-lg sm:text-2xl md:text-3xl font-semibold mb-4">
              <span className="inline-block h-0.5 w-12 theme-divider sm:w-10 md:w-12"></span>
              <span className="max-w-3xl break-words px-2">{personalInfo.title}</span>
              <span className="inline-block h-0.5 w-12 theme-divider sm:w-10 md:w-12"></span>
            </div>
          </div>

          {/* Tagline */}
          <p className="text-base sm:text-xl theme-text-secondary max-w-3xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2">
            {personalInfo.tagline}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-8 max-w-3xl mx-auto mb-10 sm:mb-12">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold theme-accent-text theme-heading-font mb-1">
                {formattedYearsOfExperience}
              </div>
              <div className="text-sm theme-text-secondary">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold theme-accent-text theme-heading-font mb-1">
                {projectsCompleted}+
              </div>
              <div className="text-sm theme-text-secondary">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold theme-accent-text theme-heading-font mb-1">
                {companiesWorked}
              </div>
              <div className="text-sm theme-text-secondary">Companies Worked</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 mb-10 sm:mb-12 w-full max-w-xl mx-auto">
            <Button
              onClick={() => scrollToSection('projects')}
              size="lg"
              className="theme-button-primary w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all hover:shadow-lg hover:scale-105"
            >
              View My Work
            </Button>
            <Button
              onClick={handleDownloadResume}
              size="lg"
              variant="outline"
              disabled={!hasResume}
              className="theme-button-outline w-full sm:w-auto border-2 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all hover:shadow-lg hover:scale-105"
            >
              <Download className="mr-2" size={20} />
              {hasResume ? 'Download Resume' : 'Resume Coming Soon'}
            </Button>
          </div>

          {/* Social Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 max-w-2xl mx-auto">
            <a
              href={`mailto:${personalInfo.email}`}
              className="theme-icon-button transition-all hover:scale-110"
              aria-label="Email"
            >
              <Mail size={24} />
            </a>
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="theme-icon-button transition-all hover:scale-110"
              aria-label="LinkedIn"
            >
              <Linkedin size={24} />
            </a>
            <a
              href="#"
              className="theme-icon-button transition-all hover:scale-110"
              aria-label="GitHub"
            >
              <Github size={24} />
            </a>
            <div className="flex basis-full justify-center sm:basis-auto items-center theme-text-secondary sm:ml-2">
              <MapPin size={20} className="mr-1" />
              <span className="text-sm text-center">{personalInfo.location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 hidden transform -translate-x-1/2 animate-bounce sm:block">
        <div className="w-6 h-10 rounded-full flex items-start justify-center p-2" style={{ border: '2px solid var(--site-accent-primary)' }}>
          <div className="w-1.5 h-3 rounded-full animate-pulse" style={{ backgroundColor: 'var(--site-accent-primary)' }}></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
